import {
  useIsParseEnabled,
  usePairedFileForKey,
  useParseForKey,
  useUploadForKey,
} from './hooks'
import { Box, Button, Checkbox, FormControlLabel } from '@mui/material'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { ConfirmDialog } from 'shared/ui'
import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import { ingestedFilesAtom } from '../../components/ingestion/atoms'
import { formatDistanceToNow } from 'date-fns'
import { sourceFilesForSelectedCourseAtom, userMessagesAtom } from './atoms'
import { useCreateTreeForKey } from '../../components/ingestion/CreateTreeButton'
import { ConfirmWithDialogButton } from 'shared/ui'

export function ActionButtons() {
  return (
    <Box>
      <ParseButton />
      <UploadButton />
      <CreateOSETreeButton />
    </Box>
  )
}

function ParseButton() {
  const [confirming, setConfirming] = useState(false)
  const parseEnabled = useIsParseEnabled()
  const parseCourse = useParseForKey()

  return (
    <>
      <Button disabled={!parseEnabled} onClick={() => setConfirming(true)}>
        Test Parse
      </Button>
      <ParseConfirmation
        doShow={confirming}
        onDone={() => setConfirming(false)}
        parseFunc={parseCourse}
      />
    </>
  )
}

function UploadButton() {
  const [confirming, setConfirming] = useState(false)
  const pairedFile = usePairedFileForKey()
  const parseEnabled = useIsParseEnabled()
  const doUpload = useUploadForKey()

  return (
    <>
      <Button disabled={!parseEnabled} onClick={() => setConfirming(true)}>
        {pairedFile
          ? `${formatDistanceToNow(new Date(pairedFile.last_modified))} ago`
          : 'Upload It'}
      </Button>
      <ParseConfirmation
        doShow={confirming}
        onDone={() => setConfirming(false)}
        parseFunc={doUpload}
      />
    </>
  )
}

function ParseConfirmation({
  doShow,
  onDone,
  parseFunc,
}: {
  doShow: boolean
  onDone: () => void
  parseFunc: (files: string[]) => Promise<unknown>
}) {
  const sourceFiles = useAtomValue(sourceFilesForSelectedCourseAtom)
  const [selectedUnits, setSelectedUnits] = useState<string[]>([])
  const setMessages = useSetAtom(userMessagesAtom)
  const doRefresh = useSetAtom(ingestedFilesAtom)

  useEffect(() => {
    if (!sourceFiles) {
      setSelectedUnits([])
    } else {
      setSelectedUnits(sourceFiles.map(({ URL }) => URL))
    }
  }, [sourceFiles])

  const onChangeChecked = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedUnits((prev) => [...prev, e.target.value])
    } else {
      setSelectedUnits((prev) => prev.filter((n) => n !== e.target.value))
    }
  }

  const onConfirm = async () => {
    setMessages([])
    await parseFunc(selectedUnits)
    doRefresh()
    onDone()
  }

  if (!sourceFiles) return null

  return (
    <ConfirmDialog
      open={doShow}
      title={'Parse Course Units'}
      onClose={onDone}
      onConfirm={onConfirm}
    >
      <Box>
        {sourceFiles.map(({ URL, key }) => {
          return (
            <Box key={key}>
              <FormControlLabel
                control={
                  <Checkbox
                    value={URL}
                    checked={selectedUnits.includes(URL)}
                    onChange={onChangeChecked}
                    name={key}
                  />
                }
                label={key.split('-')[0]}
              />
            </Box>
          )
        })}
      </Box>
    </ConfirmDialog>
  )
}

function CreateOSETreeButton() {
  const pairedFile = usePairedFileForKey()
  const createTree = useCreateTreeForKey()

  if (!pairedFile) return null
  return (
    <ConfirmWithDialogButton
      dialogTitle={'Create Tree'}
      dialogContent={
        <Box>{`Creating a tree from an ingestion that was done ${formatDistanceToNow(new Date(pairedFile.last_modified))} ago`}</Box>
      }
      onClick={() => createTree(pairedFile.key)}
    >
      Create Tree
    </ConfirmWithDialogButton>
  )
}
